import type { TFunction } from 'i18next';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { SegmentExport, TSegmentExportState } from '@feathr/blackbox';
import { Pill } from '@feathr/components';
import RunningProgress, { toRunningPercent } from '@feathr/extender/components/RunningProgress';

interface IExportStatusCellProps {
  segmentExport: SegmentExport;
}

const RUNNING_STATES = new Set<TSegmentExportState>(['pending', 'exporting', 'stopping']);

interface IExportStateDisplay {
  theme: string;
  indicatorVariant: 'solid' | 'outline';
}

/**
 * Per-state pill theme + indicator. `pending` and `exporting` share one label.
 * Labels are translated at the render site so static key extraction sees each `t()`.
 */
export const exportStateDisplay: Record<TSegmentExportState, IExportStateDisplay> = {
  pending: { theme: 'yellow', indicatorVariant: 'outline' },
  exporting: { theme: 'yellow', indicatorVariant: 'outline' },
  stopping: { theme: 'yellow', indicatorVariant: 'outline' },
  ready: { theme: 'green', indicatorVariant: 'solid' },
  failed: { theme: 'red', indicatorVariant: 'solid' },
  stopped: { theme: 'red', indicatorVariant: 'solid' },
  expired: { theme: 'neutral', indicatorVariant: 'solid' },
};

const FALLBACK_DISPLAY: IExportStateDisplay = { theme: 'neutral', indicatorVariant: 'solid' };

function getStateLabel(state: TSegmentExportState, t: TFunction): string {
  switch (state) {
    case 'pending':

    case 'exporting':
      return t('In progress');

    case 'stopping':
      return t('Stopping…');

    case 'ready':
      return t('Completed');

    case 'failed':
      return t('Failed');

    case 'stopped':
      return t('Cancelled');

    case 'expired':
      return t('Expired');

    default:
      return state;
  }
}

/** The Status cell: a running export's bar once it reports a percent, otherwise its state's chip. */
function ExportStatusCell({ segmentExport }: Readonly<IExportStatusCellProps>): JSX.Element {
  const { t } = useTranslation();
  const state = segmentExport.get('state');
  const stateLabel = getStateLabel(state, t);
  const percent = RUNNING_STATES.has(state)
    ? toRunningPercent(segmentExport.get('progress'))
    : undefined;

  if (percent !== undefined) {
    return <RunningProgress caption={stateLabel} label={t('Export progress')} percent={percent} />;
  }

  const display = exportStateDisplay[state] ?? FALLBACK_DISPLAY;

  return (
    <Pill indicatorVariant={display.indicatorVariant} theme={display.theme} variant={'secondary'}>
      {stateLabel}
    </Pill>
  );
}

export default observer(ExportStatusCell);
